import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "server-side", "total-items", "current-page", "loading"]

import { EPINIO_TYPES } from '../types';
import { useStore } from 'vuex';
import { computed, ref, onMounted, onUnmounted, watchEffect, watch } from 'vue';
import Masthead from '@shell/components/ResourceList/Masthead';
import { startPolling, stopPolling } from '../utils/polling';
import { debounce } from 'lodash';
import { makeActionMenu } from '../utils/table-formatters';
import { overrideTableRows } from '../utils/table-formatters';
import GitConfigModal from '../components/gitconfigs/GitConfigModal.vue';
import GitConfigDeleteModal from '../components/gitconfigs/GitConfigDeleteModal.vue';
import EpinioGitConfigModel from '../models/gitconfigs';


export default /*@__PURE__*/_defineComponent({
  __name: 'gitconfigs',
  props: {
    schema: {}
  },
  setup(__props: any) {

 // Keep for compatibility

const store = useStore();

const pending = ref(true);
const rows = ref<any[]>([]);

const gitConfigModal = ref<InstanceType<typeof GitConfigModal> | null>(null);
const gitConfigDeleteModal = ref<InstanceType<typeof GitConfigDeleteModal> | null>(null);

const resource: string = EPINIO_TYPES.GIT_CONFIG;
const paginationMeta = computed(() => store.getters['epinio/paginationMeta'](resource));
const currentPage = computed(() => store.getters['epinio/currentPaginationPage'](resource));

const searchQuery = ref<string>('');

const paginating = ref(false);

const canEdit = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('gitconfig_write'));
});
const canDelete = canEdit;
const canCreate = canEdit;

async function goToPage(page: number) {
  const meta = paginationMeta.value;

  if (meta && (page < 1 || page > meta.totalPages)) return;
  paginating.value = true;
  try {
    await store.dispatch('epinio/goToPage', { type: resource, page });
  } finally {
    paginating.value = false;
  }
}

const onSearch = debounce(async (query: string) => {
  paginating.value = true;
  try {
    await store.dispatch('epinio/search', { type: resource, query });
  } finally {
    paginating.value = false;
  }
}, 500);

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

watchEffect(() => {
  const all = store.getters['epinio/all'](EPINIO_TYPES.GIT_CONFIG) as any[];

  // Touch meta so _MERGE polling (which deletes/re-adds all properties) re-runs this effect
  all.forEach((row: any) => { void row.meta; });

  // Filter empty rows that are added during delete
  const filtered = all.filter((row) => {
    if (!row.id) return false;
    else return true;
  });

  // Build the row action menu with RBAC gating. The model already gates the
  // base actions; here we inject the modal-driven Edit/Delete entries only
  // when the user has git config write permissions.
  const rowActions = () => {
    const out: any[] = [];

    if (canDelete.value) {
      out.push({
        action: 'removeGitConfig',
        enabled: true,
        label: 'Delete',
      });
    }


    return out;
  };

  const overrideProps = [
    {
      prop: 'availableActions',
      value: rowActions,
      conditionFn: () => true,
    },
    {
      prop: 'removeGitConfig',
      value: (row: EpinioGitConfigModel) => () => {
        gitConfigDeleteModal.value?.openDelete(row);
      },
      conditionFn: () => canDelete.value,
    },
  ];

  const processedRows = overrideTableRows(filtered, overrideProps);

  rows.value = [...processedRows];
});

onMounted(async () => {
  store.dispatch('epinio/me');
  await store.dispatch(`epinio/findAll`, { type: EPINIO_TYPES.GIT_CONFIG });
  pending.value = false;
  startPolling(['gitconfigs'], store);
});

onUnmounted(() => {
  stopPolling(['gitconfigs']);
});

const columns = [
  {
    field: 'meta.name',
    label: 'Name'
  },
  {
    field: 'provider',
    label: 'Provider',
    formatter: 'gitProviders'
  },
  {
    field: 'url',
    label: 'URL'
  },
  {
    field:     'meta.createdAt',
    label:     'Age',
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Masthead), {
        schema: _ctx.schema,
        resource: _unref(resource)
      }, {
        createButton: _withCtx(() => [
          (_unref(canCreate))
            ? (_openBlock(), _createElementBlock("trailhand-button", {
                key: 0,
                variant: "primary",
                size: "large",
                onButtonClick: _cache[0] || (_cache[0] = ($event: any) => (gitConfigModal.value?.openCreate()))
              }, _toDisplayString(_ctx.t('generic.create')), 33))
            : (_openBlock(), _createElementBlock("div", _hoisted_2))
        ]),
        _: 1
      }, 8, ["schema", "resource"]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("trailhand-text-input", {
          value: searchQuery.value,
          placeholder: "Search...",
          onTextInputChange: _cache[1] || (_cache[1] = (e) => searchQuery.value = e.detail.value)
        }, null, 40, _hoisted_4)
      ]),
      _cache[4] || (_cache[4] = _createTextVNode()),
      _createElementVNode("trailhand-table", {
        ref: (el) => { if (el) el.renderActions = _unref(makeActionMenu); },
        rows: rows.value,
        columns: columns,
        searchable: false,
        "server-side": !!paginationMeta.value,
        "total-items": paginationMeta.value?.totalItems ?? rows.value.length,
        "current-page": currentPage.value,
        loading: pending.value || paginating.value,
        "key-field": "id",
        onPageChange: _cache[2] || (_cache[2] = (e) => goToPage(e.detail.page))
      }, null, 40, _hoisted_5)
    ]),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createVNode(GitConfigModal, {
      ref_key: "gitConfigModal",
      ref: gitConfigModal
    }, null, 512),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createVNode(GitConfigDeleteModal, {
      ref_key: "gitConfigDeleteModal",
      ref: gitConfigDeleteModal
    }, null, 512)
  ], 64))
}
}

})